import React, { useState, useEffect, forwardRef } from "react";
import { Input } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { InputProps } from "antd/lib/input";

interface SSNMaskedInputProps extends Omit<InputProps, "onChange" | "value"> {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  requireConfirmation?: boolean;
  confirmationClassName?: string;
  onValidationChange?: (isValid: boolean) => void;
}

// IMPORTANT: We use forwardRef to properly integrate with Form.Item
const SSNMaskedInput = forwardRef<any, SSNMaskedInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled,
      className,
      requireConfirmation,
      confirmationClassName,
      onValidationChange,
      ...restProps
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [internalValue, setInternalValue] = useState<string>(value || "");
    const [confirmationValue, setConfirmationValue] = useState<string>("");
    const [confirmationVisible, setConfirmationVisible] =
      useState<boolean>(false);
    const [originalValue, setOriginalValue] = useState<string>(value || "");

    // Format SSN with dashes
    const formatSSN = (val: string): string => {
      const cleaned = val.replace(/\D/g, "");
      const match = cleaned.match(/^(\d{0,3})(\d{0,2})(\d{0,4})$/);
      if (match) {
        return [match[1], match[2], match[3]].filter(Boolean).join("-");
      }
      return val;
    };

    // Mask the SSN value
    const maskSSN = (val: string): string => {
      if (!val) return "";
      // Replace all digits with bullets, keep dashes
      return val.replace(/\d/g, "•");
    };

    // Determine what to display - ensure prefilled values are immediately masked
    const currentInternalValue =
      value !== undefined ? value || "" : internalValue;
    const displayValue = isVisible
      ? currentInternalValue
      : maskSSN(currentInternalValue);
    const confirmationDisplayValue = confirmationVisible
      ? confirmationValue
      : maskSSN(confirmationValue);

    // Helper function to create input handler for both main and confirmation inputs
    const createInputHandler =
      (
        currentValue: string,
        setCurrentValue: (value: string) => void,
        isVisible: boolean,
        displayValue: string,
        onChangeCallback?: (value: string) => void
      ) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // If the user is typing in the masked state, we need to handle it differently
        if (!isVisible && currentValue) {
          // Handle backspace/delete
          if (inputValue.length < displayValue.length) {
            // Get the current digits only
            const currentDigits = currentValue.replace(/\D/g, "");

            // Determine how many characters were deleted
            const deletedCount = displayValue.length - inputValue.length;

            // For single character deletion
            if (deletedCount === 1) {
              // Find which position was deleted
              const deletedPosition = findDeletedPosition(
                displayValue,
                inputValue
              );

              // If a dash was deleted, delete the digit before it
              if (displayValue[deletedPosition] === "-") {
                const newDigits = removeDigitAtPosition(
                  currentDigits,
                  currentValue,
                  deletedPosition - 1
                );
                const formatted = formatSSN(newDigits);
                setCurrentValue(formatted);
                if (onChangeCallback) onChangeCallback(formatted);
              } else {
                // A bullet (digit) was deleted
                const newDigits = removeDigitAtPosition(
                  currentDigits,
                  currentValue,
                  deletedPosition
                );
                const formatted = formatSSN(newDigits);
                setCurrentValue(formatted);
                if (onChangeCallback) onChangeCallback(formatted);
              }
            } else {
              // Multiple characters deleted (e.g., selection delete)
              // For simplicity, remove from the end
              const newDigits = currentDigits.slice(0, -deletedCount);
              const formatted = formatSSN(newDigits);
              setCurrentValue(formatted);
              if (onChangeCallback) onChangeCallback(formatted);
            }
            return;
          }

          // For new input, append to the existing value
          const lastChar = inputValue.slice(-1);
          if (/\d/.test(lastChar)) {
            const currentDigits = currentValue.replace(/\D/g, "");
            if (currentDigits.length >= 9) return;

            const newDigits = currentDigits + lastChar;
            const formatted = formatSSN(newDigits);
            setCurrentValue(formatted);

            if (onChangeCallback) {
              onChangeCallback(formatted);
            }
          }
          return;
        }

        // Normal input handling when visible
        const cleaned = inputValue.replace(/\D/g, "");

        // Limit to 9 digits
        if (cleaned.length > 9) return;

        const formatted = formatSSN(cleaned);
        setCurrentValue(formatted);

        if (onChangeCallback) {
          onChangeCallback(formatted);
        }
      };

    // Helper function to find which position was deleted
    const findDeletedPosition = (before: string, after: string): number => {
      for (let i = 0; i < before.length; i++) {
        if (i >= after.length || before[i] !== after[i]) {
          return i;
        }
      }
      return before.length - 1;
    };

    // Helper function to remove a digit at a specific visual position
    const removeDigitAtPosition = (
      digits: string,
      formatted: string,
      position: number
    ): string => {
      let digitIndex = 0;
      let visualIndex = 0;

      for (let i = 0; i < formatted.length && visualIndex <= position; i++) {
        if (formatted[i] !== "-") {
          if (visualIndex === position) {
            // Remove this digit
            return digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
          }
          digitIndex++;
        }
        visualIndex++;
      }

      // If we didn't find it, remove the last digit
      return digits.slice(0, -1);
    };

    // Sync with external value changes (from Form.Item)
    useEffect(() => {
      if (value !== undefined && value !== internalValue) {
        setInternalValue(value || "");
        // Only update originalValue if this is the first time we're setting it (prefill scenario)
        if (!originalValue && value) {
          setOriginalValue(value);
        }
      }
    }, [value, internalValue, originalValue]);

    // Validate confirmation when either value changes
    useEffect(() => {
      if (requireConfirmation) {
        // Check if confirmation field should be visible
        const shouldShowConfirmation =
          currentInternalValue.length === 11 && // SSN is complete
          (originalValue ? currentInternalValue !== originalValue : true); // Different from saved OR new entry

        if (!shouldShowConfirmation) {
          // Confirmation field is hidden, validation passes
          if (onValidationChange) {
            onValidationChange(true);
          }
        } else {
          // Confirmation field is visible, validate it
          if (!confirmationValue) {
            if (onValidationChange) {
              onValidationChange(false);
            }
          } else {
            // If confirmation has value, check if it matches
            const isValid = currentInternalValue === confirmationValue;
            if (onValidationChange) {
              onValidationChange(isValid);
            }
          }
        }
      } else {
        if (onValidationChange) {
          onValidationChange(true);
        }
      }
    }, [
      currentInternalValue,
      confirmationValue,
      requireConfirmation,
      onValidationChange,
      originalValue,
    ]);

    // Clear confirmation value when SSN changes from original (confirmation field appears)
    useEffect(() => {
      if (
        requireConfirmation &&
        originalValue &&
        currentInternalValue !== originalValue
      ) {
        // SSN has changed from original, clear confirmation to force re-entry
        setConfirmationValue("");
      }
    }, [requireConfirmation, originalValue, currentInternalValue]);
    // Note: confirmationValue is intentionally NOT in dependencies to avoid clearing on every keystroke

    const toggleVisibility = () => {
      setIsVisible(!isVisible);
    };

    const toggleConfirmationVisibility = () => {
      setConfirmationVisible(!confirmationVisible);
    };

    // Create specific handlers for main and confirmation inputs
    const handleChange = createInputHandler(
      currentInternalValue,
      setInternalValue,
      isVisible,
      displayValue,
      onChange
    );

    const handleConfirmationChange = createInputHandler(
      confirmationValue,
      setConfirmationValue,
      confirmationVisible,
      confirmationDisplayValue
    );

    return (
      <>
        <Input
          {...restProps}
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder || "•••-••-••••"}
          disabled={disabled}
          className={className}
          maxLength={11} // 9 digits + 2 dashes
          suffix={
            <button
              type="button"
              onClick={toggleVisibility}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                color: "#8c8c8c",
              }}
              disabled={disabled}
            >
              {isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          }
        />
        {/* Confirmation Input - Only rendered if requireConfirmation is true and the user 
        has completed typing the original input. In addition, this field is not rendered on return trips
        to the step unless the user modifies the saved SSN. This is not a form item (it has no ref), 
        so it will not be saved to the application. If the values do not match, an error message will be
        displayed. */}
        {requireConfirmation &&
          currentInternalValue.length === 11 &&
          (originalValue ? currentInternalValue !== originalValue : true) && (
            <div style={{ marginTop: "8px" }}>
              <Input
                value={confirmationDisplayValue}
                onChange={handleConfirmationChange}
                placeholder={"Confirm SSN"}
                disabled={disabled}
                className={confirmationClassName}
                maxLength={11} // 9 digits + 2 dashes
                suffix={
                  <button
                    type="button"
                    onClick={toggleConfirmationVisibility}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      color: "#8c8c8c",
                    }}
                    disabled={disabled}
                  >
                    {confirmationVisible ? (
                      <EyeInvisibleOutlined />
                    ) : (
                      <EyeOutlined />
                    )}
                  </button>
                }
              />
            </div>
          )}
      </>
    );
  }
);

SSNMaskedInput.displayName = "SSNMaskedInput";

export default SSNMaskedInput;
